import { Fragment, useState } from 'react';
import { Badge, Card, EmptyState, Loading, ModuleHeader, SearchSelect, useAsync } from '../../components/ui';
import { api, downloadFile } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { IconDownload, IconShield } from '../../components/icons';

/**
 * Beschriftungen für die Gegenüberstellung.
 *
 * Im Protokoll stehen die Spaltennamen der Datenbank, weil `changeSet` mit den
 * Datensätzen arbeitet und nicht wissen kann, wie das Formular die Felder
 * nennt. Was hier fehlt, wird als Spaltenname gezeigt – lieber technisch als
 * gar nicht.
 */
const FELD_LABELS: Record<string, string> = {
  title: 'Titel',
  description: 'Beschreibung',
  location_detail: 'Ort im Objekt',
  property_id: 'Objekt',
  customer_id: 'Auftraggeber',
  category_id: 'Kategorie',
  criticality_id: 'Dringlichkeit',
  channel_id: 'Meldekanal',
  estimated_minutes: 'Geschätzte Minuten',
  scheduled_start: 'Geplanter Beginn',
  scheduled_end: 'Geplantes Ende',
  due_at: 'Fällig am',
  respond_by: 'Reaktion bis',
  status: 'Status',
  first_name: 'Vorname',
  last_name: 'Nachname',
  email: 'E-Mail',
  phone: 'Telefon',
  is_active: 'Aktiv',
  name: 'Name',
  color: 'Farbe',
  street: 'Straße',
  zip: 'PLZ',
  city: 'Ort',
  country: 'Land',
  notes: 'Notizen',
  permissionCount: 'Anzahl Rechte',
};

/**
 * Ein einzelner Wert der Gegenüberstellung, lesbar gemacht.
 *
 * Einstellungen und Empfängerlisten werden serverseitig als JSON-Text
 * abgelegt, weil sie sonst nicht vergleichbar wären. Hier stünde dann
 * `"aktiv"` samt Anführungszeichen oder `[3,7]` – deshalb wird JSON-Text
 * zurückverwandelt, bevor er angezeigt wird.
 */
function feldwert(value: unknown, feld = ''): string {
  let wert = value;
  if (typeof wert === 'string' && /^[[{"]|^(true|false|null|-?\d)/.test(wert)) {
    try {
      wert = JSON.parse(wert);
    } catch {
      /* kein JSON – dann eben unverändert */
    }
  }
  if (wert === null || wert === undefined || wert === '') return '–';
  if (wert === true) return 'Ja';
  if (wert === false) return 'Nein';
  // 0/1 nur dort als Ja/Nein lesen, wo der Feldname ein Schaltfeld verrät –
  // sonst würde aus `estimated_minutes: 1` ein „Ja“.
  if ((wert === 0 || wert === 1) && /^(is_|has_|allow_|requires_|auto_)|enabled$/.test(feld)) {
    return wert === 1 ? 'Ja' : 'Nein';
  }
  if (typeof wert === 'object') return JSON.stringify(wert);
  return String(wert);
}

type Zeitraum = 'monat' | 'quartal' | 'jahr' | 'frei';

/** Von/Bis für die Schnellwahl – jeweils der laufende Zeitraum bis heute. */
function zeitraumGrenzen(art: Zeitraum): { from: string; to: string } | null {
  if (art === 'frei') return null;
  const heute = new Date();
  // Von Hand zusammengesetzt statt über `toISOString()`: das rechnet nach UTC
  // um, und der 1. August 00:00 Uhr mitteleuropäischer Sommerzeit wäre dort
  // noch der 31. Juli – der Zeitraum begänne einen Tag zu früh.
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const start =
    art === 'monat'
      ? new Date(heute.getFullYear(), heute.getMonth(), 1)
      : art === 'quartal'
        ? new Date(heute.getFullYear(), Math.floor(heute.getMonth() / 3) * 3, 1)
        : new Date(heute.getFullYear(), 0, 1);
  return { from: iso(start), to: iso(heute) };
}

/** Protokoll sicherheitsrelevanter Aktionen (Kap. 10). */
export function AuditPage() {
  const [filter, setFilter] = useState({ action: '', from: '', to: '' });
  const [zeitraum, setZeitraum] = useState<Zeitraum>('frei');
  const [detailliert, setDetailliert] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const log = useAsync(() => api.get('/api/audit', { ...filter, limit: 300 }), [JSON.stringify(filter)]);

  const groups = [
    { value: '', label: 'Alle Aktionen' },
    { value: 'login', label: 'Anmeldungen' },
    { value: 'password', label: 'Passwörter' },
    { value: 'permission', label: 'Rechteänderungen' },
    { value: 'user', label: 'Benutzerverwaltung' },
    { value: 'role', label: 'Rollen' },
    { value: 'group', label: 'Gruppen' },
    { value: 'task', label: 'Aufträge' },
    { value: 'order', label: 'Bestellungen' },
    { value: 'security', label: 'Sicherheit' },
  ];

  function waehleZeitraum(art: Zeitraum) {
    setZeitraum(art);
    const grenzen = zeitraumGrenzen(art);
    if (grenzen) setFilter({ ...filter, ...grenzen });
  }

  function umschalten(id: number) {
    const naechste = new Set(expanded);
    if (naechste.has(id)) naechste.delete(id);
    else naechste.add(id);
    setExpanded(naechste);
  }

  function exportieren(format: 'csv' | 'pdf') {
    const stand = detailliert ? 'detail' : 'uebersicht';
    downloadFile(`/api/audit/export.${format}`, `protokoll-${stand}.${format}`, {
      ...filter,
      mode: detailliert ? 'detail' : 'overview',
    }).catch(() => window.alert('Export fehlgeschlagen.'));
  }

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconShield width={22} height={22} />}
        title="Audit-Log"
        subtitle="Nachvollziehbare Protokollierung aller sicherheits- und rechterelevanten Vorgänge – wer hat wann was geändert."
        pill="Protokoll"
        tone="info"
      />

      <Card noBody>
        <div className="card-body tight row" style={{ gap: 10 }}>
          <SearchSelect
            value={String(filter.action ?? '')}
            onChange={(wert) => setFilter({ ...filter, action: wert })}
            options={(groups ?? []).map((group: any) => ({ value: String(group.value), label: String(group.label) }))}
            clearable={false}
            style={{ width: 220 }}
          />
          <div className="pill-tabs">
            {(['monat', 'quartal', 'jahr', 'frei'] as Zeitraum[]).map((art) => (
              <button
                key={art}
                type="button"
                className={`pill-tab${zeitraum === art ? ' active' : ''}`}
                onClick={() => waehleZeitraum(art)}
              >
                {art === 'monat' ? 'Monat' : art === 'quartal' ? 'Quartal' : art === 'jahr' ? 'Jahr' : 'Frei'}
              </button>
            ))}
          </div>
          <input
            type="date"
            value={filter.from}
            onChange={(event) => {
              setZeitraum('frei');
              setFilter({ ...filter, from: event.target.value });
            }}
            title="Von"
          />
          <input
            type="date"
            value={filter.to}
            onChange={(event) => {
              setZeitraum('frei');
              setFilter({ ...filter, to: event.target.value });
            }}
            title="Bis"
          />
          <label className="checkbox-row" title="Je geändertem Feld eine eigene Zeile">
            <input type="checkbox" checked={detailliert} onChange={(e) => setDetailliert(e.target.checked)} />
            Detailliert
          </label>
          <button className="btn sm" onClick={() => exportieren('csv')}>
            <IconDownload width={15} height={15} /> CSV
          </button>
          <button className="btn sm" onClick={() => exportieren('pdf')}>
            <IconDownload width={15} height={15} /> PDF
          </button>
        </div>

        {log.loading ? (
          <Loading />
        ) : !log.data?.length ? (
          <EmptyState title="Keine Einträge" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 28 }} />
                  <th>Zeitpunkt</th>
                  <th>Aktion</th>
                  <th>Benutzer</th>
                  <th>Objekt</th>
                  <th>Beschreibung</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {log.data.map((entry: any) => (
                  <Fragment key={entry.id}>
                    <tr className="clickable" onClick={() => umschalten(entry.id)}>
                      <td className="small subtle">{expanded.has(entry.id) ? '▾' : '▸'}</td>
                      <td className="small nowrap">{formatDateTime(entry.created_at)}</td>
                      <td>
                        <Badge variant={variantFor(entry.action)}>{entry.action}</Badge>
                      </td>
                      <td className="small truncate" style={{ maxWidth: 220 }}>{entry.user_label ?? 'System'}</td>
                      <td className="small subtle">
                        {entry.entity_type ? `${entry.entity_type} #${entry.entity_id}` : '–'}
                      </td>
                      <td className="small truncate" style={{ maxWidth: 340 }}>{entry.summary ?? '–'}</td>
                      <td className="small mono subtle">{entry.ip ?? '–'}</td>
                    </tr>
                    {expanded.has(entry.id) && (
                      <tr>
                        <td colSpan={7} className="audit-detail">
                          <AuditDetails entry={entry} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * Aufgeklappter Inhalt einer Protokollzeile.
 *
 * Drei Fälle: eine Gegenüberstellung (seit der Erfassung von Vorher/Nachher),
 * sonstige Detaildaten aus der Zeit davor, und gar keine. Der dritte Fall
 * bekommt einen Hinweis statt einer leeren Tabelle – nachträglich lässt sich
 * dort nichts ergänzen, und ein leerer Kasten sähe nach einem Fehler aus.
 */
function AuditDetails({ entry }: { entry: any }) {
  let details: any = null;
  try {
    details = entry.details ? JSON.parse(entry.details) : null;
  } catch {
    details = entry.details;
  }

  if (!details) {
    return (
      <p className="small subtle" style={{ margin: 0 }}>
        Zu diesem Vorgang wurden keine Einzelheiten erfasst. Vorher/Nachher wird erst seit der Umstellung des
        Protokolls mitgeschrieben; ältere Einträge lassen sich rückwirkend nicht ergänzen.
      </p>
    );
  }

  if (details.before && details.after) {
    const felder = Object.keys(details.before);
    return (
      <table className="data" style={{ margin: 0 }}>
        <thead>
          <tr>
            <th>Feld</th>
            <th>Davor</th>
            <th>Danach</th>
          </tr>
        </thead>
        <tbody>
          {felder.map((feld) => (
            <tr key={feld}>
              <td className="small">{FELD_LABELS[feld] ?? feld}</td>
              <td className="small subtle">{feldwert(details.before[feld], feld)}</td>
              <td className="small">
                <strong>{feldwert(details.after[feld], feld)}</strong>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <pre className="small mono" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
      {typeof details === 'string' ? details : JSON.stringify(details, null, 2)}
    </pre>
  );
}

function variantFor(action: string): string {
  if (action.startsWith('login.failed') || action.includes('deleted') || action.includes('2fa_failed')) return 'danger';
  if (action.startsWith('permission') || action.includes('password')) return 'warning';
  if (action.startsWith('login.success')) return 'success';
  return '';
}
